import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Button, IconButton, TextField, Switch, Select, MenuItem } from '@mui/material';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { normalizeExperiment } from './experimentNormalization';

const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

function RunningExperiments() {
    const [experiments, setExperiments] = useState([]);
    // Sorting state
    const [sortField, setSortField] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const { getAccessTokenSilently } = useAuth0();
    
    // Sorted experiments based on field and order
    const sortedExperiments = useMemo(() => {
        const arr = [...experiments];
        arr.sort((a, b) => {
            let av, bv;
            if (sortField === 'title') {
                av = (a.title || '').toLowerCase(); bv = (b.title || '').toLowerCase();
                return av.localeCompare(bv);
            }
            if (sortField === 'date') {
                av = new Date(a.date).getTime(); bv = new Date(b.date).getTime();
                return av - bv;
            }
            // sessions
            av = a.sessions || 0; bv = b.sessions || 0;
            return av - bv;
        });
        if (sortOrder === 'desc') arr.reverse();
        return arr;
    }, [experiments, sortField, sortOrder]);

    // Callback to update a single experiment in state (supports partial PATCH responses)
    const updateExperiment = (updated) => {
        if (!updated || !updated.expt_id) return;
        setExperiments(prev => prev.map(exp => {
            if (exp.expt_id !== updated.expt_id) return exp;
            // Merge to preserve existing fields (server may return only the patched subset: e.g., { active: true })
            const merged = { ...exp, ...updated };
            // If the incoming update omits title but old one exists, keep it explicitly
            if (!('title' in updated) && exp.title) {
                merged.title = exp.title;
            }
            // Recompute normalized view so title display stays consistent
            merged._normalized = normalizeExperiment(merged);
            return merged;
        }));
    };
    // Callback to remove an experiment from state
    const removeExperiment = (id) => {
        setExperiments(prev => prev.filter(exp => exp.expt_id !== id));
    };

    useEffect(() => {
        const fetchExperiments = async () => {
            try {
                const token = await getAccessTokenSilently();
                const response = await axios.get(`${API_URL}/api/experiment`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Normalize experiments (without mutating original structure for legacy compatibility)
                const normalized = response.data.map(e => ({ ...e, _normalized: normalizeExperiment(e) }));
                setExperiments(normalized);
            } catch (error) {
                console.error('Error fetching running experiments:', error);
            }
        };
        fetchExperiments();
    }, [getAccessTokenSilently]);
    // Separate active and inactive lists
    const activeExperiments = useMemo(
        () => sortedExperiments.filter(exp => exp.active),
        [sortedExperiments]
    );
    const inactiveExperiments = useMemo(
        () => sortedExperiments.filter(exp => !exp.active),
        [sortedExperiments]
    );

    const ExperimentCard = ({ experiment, onUpdate, onDelete }) => {
        // Track title edit mode and active state
        const [isEditing, setIsEditing] = useState(false);
    const displayTitle = experiment._normalized?.title || experiment.title;
    const [editedTitle, setEditedTitle] = useState(displayTitle || '');
        const [isActive, setIsActive] = useState(experiment.active || false);
        const handleTitleChange = e => setEditedTitle(e.target.value);
        const cancelEdit = () => {
            setIsEditing(false);
            // Use normalized title (covers cases where title lives nested in task parameters initially)
            setEditedTitle(experiment._normalized?.title || experiment.title || '');
        };
        const confirmEdit = async () => {
            try {
                const token = await getAccessTokenSilently();
                const res = await axios.patch(
                    `${API_URL}/api/experiment/${experiment.expt_id}`,
                    { title: editedTitle, active: isActive },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                onUpdate(res.data);
                // Sync local editedTitle with whatever the merged state will contain; rely on displayTitle fallback
                setEditedTitle(editedTitle || '');
            } catch (err) {
                console.error('Error updating title:', err);
            }
            setIsEditing(false);
        };
        const handleActiveChange = async e => {
            const newActive = e.target.checked;
            setIsActive(newActive);
            try {
                const token = await getAccessTokenSilently();
                const res = await axios.patch(
                    `${API_URL}/api/experiment/${experiment.expt_id}`,
                    { active: newActive },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                onUpdate(res.data);
            } catch (err) {
                console.error('Error updating active:', err);
            }
        };
        // Handle experiment deletion
        const handleDelete = async () => {
            if (!window.confirm('Delete this experiment?')) return;
            try {
                const token = await getAccessTokenSilently();
                await axios.delete(
                    `${API_URL}/api/experiment/${experiment.expt_id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                onDelete(experiment.expt_id);
            } catch (err) {
                console.error('Error deleting experiment:', err);
            }
        };

        return (
            <Box display="flex" justifyContent="space-between" alignItems="center" border={1} padding={2} marginBottom={2}>
            <Box>
                {/* Title with inline edit */}
                <Box display="flex" alignItems="center">
                    {isEditing ? (
                        <>
                            <TextField value={editedTitle} onChange={handleTitleChange} size="small" variant="standard" />
                            <IconButton size="small" onClick={confirmEdit}><CheckIcon style={{ color: 'green' }} fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={cancelEdit}><CloseIcon style={{ color: 'red' }} fontSize="small" /></IconButton>
                        </>
                    ) : (
                        <>
                            <Typography variant="h6" gutterBottom>{experiment._normalized?.title || experiment.title || 'Untitled Experiment'}</Typography>
                            <IconButton size="small" onClick={() => setIsEditing(true)}><EditIcon fontSize="small" /></IconButton>
                        </>
                    )}
                </Box>
                {/* Show ID below title */}
                <Typography variant="body1" gutterBottom>ID: {experiment.expt_id}</Typography>
                <Typography variant="body1" gutterBottom>
                Task Type: {experiment.task.type}
                </Typography>
                <Typography variant="body1" gutterBottom>
                Agents: {experiment.agents && experiment.agents.length > 0 ? experiment.agents.map((agent) => agent.name).join(', ') : 'No agents'}
                </Typography>
                <Typography variant="body1" gutterBottom>
                Date: {experiment.date}
                </Typography>
                <Typography variant="body1" gutterBottom>
                    Sessions: {experiment.sessions}
                </Typography>
                {/* Active toggle */}
                <Box display="flex" alignItems="center" marginTop={1}>
                    <Switch checked={isActive} onChange={handleActiveChange} />
                    <Typography variant="body2">{isActive ? 'Active' : 'Inactive'}</Typography>
                </Box>
             </Box>
             <Box display={'flex'} flexDirection={'column'} alignItems={'center'} padding={2}>
                <Box>
                    <Button color="inherit" component={Link} to={`/experiments/${experiment.expt_id}`} style={{backgroundColor: 'green', color: 'white', margin:'4px', padding: '10px 20px'}}> Open </Button>
                </Box>
                <Box>
                    <Button color="inherit" component={Link} to={`/experiments/${experiment.expt_id}/details`} style={{backgroundColor: 'blue', color: 'white', margin:'4px', padding: '10px 20px'}}> Manage </Button>
                </Box>
                <Box>
                    <Button onClick={handleDelete} style={{backgroundColor: 'red', color: 'white', margin:'4px', padding: '10px 20px'}}> Delete </Button>
                </Box>
             </Box>
         </Box>
     );
    };

    return (
        <Box>
            {/* Header with sorting controls */}
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                <Typography variant="h4">Running Experiments</Typography>
                <Box display="flex" alignItems="center">
                    <Typography variant="body1" style={{ marginRight: 8 }}>Sort by</Typography>
                    <Select
                        value={sortField}
                        size="small"
                        onChange={e => setSortField(e.target.value)}
                        style={{ marginRight: 4 }}
                    >
                        <MenuItem value="title">Name</MenuItem>
                        <MenuItem value="date">Date</MenuItem>
                        <MenuItem value="sessions">Sessions</MenuItem>
                    </Select>
                    <IconButton size="small" onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}> 
                        {sortOrder === 'asc'
                            ? <ArrowUpwardIcon fontSize="small" />
                            : <ArrowDownwardIcon fontSize="small" />}
                    </IconButton>
                </Box>
            </Box>
            {/* Experiment cards list */}
            {/* Active experiments section */}
            <Typography variant="h5" gutterBottom>Active Experiments</Typography>
            {activeExperiments.length > 0 ? activeExperiments.map(experiment => (
                <ExperimentCard key={experiment.expt_id} experiment={experiment} onUpdate={updateExperiment} onDelete={removeExperiment} />
            )) : <Typography variant="body2" color="textSecondary">No active experiments</Typography>}
            {/* Inactive experiments section */}
            <Typography variant="h5" gutterBottom style={{ marginTop: 16 }}>Inactive Experiments</Typography>
            {inactiveExperiments.length > 0 ? inactiveExperiments.map(experiment => (
                <ExperimentCard key={experiment.expt_id} experiment={experiment} onUpdate={updateExperiment} onDelete={removeExperiment} />
            )) : <Typography variant="body2" color="textSecondary">No inactive experiments</Typography>}
        </Box>
    );
 }

 export default RunningExperiments;
